import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./ProductUpsert.module.css";
import {
  PostRadioButton,
  Header,
  Footer,
  useConfirm,
  DoubleRadioButton,
} from "@/components";
import { AxiosForm } from "@/Axios";
import { useNavigate, useParams } from "react-router-dom";
import { INSERT_TAG } from "..";
import { decoded } from "@/utils";
import checkedImg from "@assets/images/check.png";
import {
  useLoadReset,
  useDebouncedCallback,
  useDimLoading,
  useDimLoadComplete,
} from "@/hooks";

export default function ProductUpsert() {
  const dimLoading = useDimLoading();
  const dimLoadComplete = useDimLoadComplete();
  const loadReset = useLoadReset();
  const { tag, info } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const defaultState = {
    productsNm: "",
    imageFile: {},
    description: "",
    isView: true,
    preview: "",
    headerTitle: "상품 등록",
    hasImage: false,
    buttonNm: "등록",
    isInsert: true,
    id: "",
    isGroupBuy: true,
    isLeft: false,
    isRight: false,
    leftButtonNm: "",
    rightButtonNm: "",
    leftLink: "",
    rightLink: "",
  };
  const [state, setState] = useState(defaultState);
  const resetState = () => {
    setState(defaultState);
    loadReset();
  };
  const fileRef = useRef();

  const triggerFileInput = () => {
    if (!state.preview) {
      fileRef.current.click();
    }
  };
  const fileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setState((s) => ({
        ...s,
        imageFile: file,
        preview: URL.createObjectURL(file),
        hasImage: true,
      }));
    }
    // 파일 선택 후 초기화
    e.target.value = "";
  };
  const removeImage = () => {
    setState((s) => ({ ...s, imageFile: {}, preview: "" }));
  };
  const changeProductName = (e) => {
    setState((s) => ({ ...s, productsNm: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };
  const changeDescription = (e) => {
    handleInput(e);
    setState((s) => ({ ...s, description: e.target.value }));
  };
  const radioOnOff = useCallback(
    (onOff) => {
      setState((s) => ({ ...s, isView: onOff }));
    },
    [state.isView]
  );
  const productsOnOff = useCallback(
    (onOff) => {
      setState((s) => ({ ...s, isGroupBuy: onOff }));
    },
    [state.isGroupBuy]
  );
  const insert = useDebouncedCallback(() => {
    if ((state.hasImage && !state.imageFile?.name) || !state.productsNm) {
      confirm("필수 정보를 입력해주세요");
      return;
    }
    const formData = new FormData();
    formData.append("imageFile", state.imageFile);
    formData.append("hasImage", state.hasImage);
    formData.append("productsNm", state.productsNm);
    formData.append("description", state.description);
    formData.append("isView", state.isView);
    formData.append("isGroupBuy", state.isGroupBuy);
    if (state.isLeft) {
      if (!state.leftButtonNm || !state.leftLink) {
        confirm("좌측 버튼 정보를 입력해주세요");
        return;
      }
      formData.append("leftButtonNm", state.leftButtonNm);
      formData.append("leftLink", state.leftLink);
    }
    if (state.isRight) {
      if (!state.rightButtonNm || !state.rightLink) {
        confirm("우측 버튼 정보를 입력해주세요");
        return;
      }
      formData.append("rightButtonNm", state.rightButtonNm);
      formData.append("rightLink", state.rightLink);
    }

    dimLoading();
    AxiosForm.post("/api/product-list/insert", formData).then((res) => {
      if (res.data?.errMsg) {
        dimLoadComplete(() => {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        });

        return;
      }
      dimLoadComplete(() => {
        confirm(
          `등록이 완료되었습니다.\n확인을 누르면 상품 목록으로 이동합니다.`,
          () => {
            navigate("/product-list", { replace: true });
          }
        );
      });
    });
  });

  const update = useDebouncedCallback(() => {
    if ((state.hasImage && !state.imageFile?.name) || !state.productsNm) {
      confirm("필수 정보를 입력해주세요");
      return;
    }

    const formData = new FormData();
    formData.append("hasImage", state.hasImage);
    formData.append("index", state.id);
    formData.append("imageFile", state.imageFile);
    formData.append("productsNm", state.productsNm);
    formData.append("description", state.description);
    formData.append("isView", state.isView);
    formData.append("isGroupBuy", state.isGroupBuy);
    if (state.isLeft) {
      if (!state.leftButtonNm || !state.leftLink) {
        confirm("좌측 버튼 정보를 입력해주세요");
        return;
      }
      formData.append("leftButtonNm", state.leftButtonNm);
      formData.append("leftLink", state.leftLink);
    }
    if (state.isRight) {
      if (!state.rightButtonNm || !state.rightLink) {
        confirm("우측 버튼 정보를 입력해주세요");
        return;
      }
      formData.append("rightButtonNm", state.rightButtonNm);
      formData.append("rightLink", state.rightLink);
    }

    dimLoading();
    AxiosForm.post("/api/product-list/update", formData).then((res) => {
      if (res.data?.errMsg) {
        dimLoadComplete(() => {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        });
        return;
      }
      dimLoadComplete(() => {
        confirm(
          `수정이 완료되었습니다.\n확인을 누르면 상품 목록으로 이동합니다.`,
          () => {
            navigate("/product-list", { replace: true });
          }
        );
      });
    });
  });
  const handleInput = (e) => {
    e.target.style.height = "auto"; // 먼저 높이를 초기화해야 함
    e.target.style.height = `${e.target.scrollHeight}px`;
  };
  const leftClick = () => {
    setState((s) => ({ ...s, isLeft: !s.isLeft }));
  };

  const rightClick = () => {
    setState((s) => ({ ...s, isRight: !s.isRight }));
  };

  const leftBtnChange = (e) => {
    setState((s) => ({ ...s, leftButtonNm: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const leftLinkChange = (e) => {
    setState((s) => ({ ...s, leftLink: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const rightBtnChange = (e) => {
    setState((s) => ({ ...s, rightButtonNm: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const rightLinkChange = (e) => {
    setState((s) => ({ ...s, rightLink: e.target.value }));
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  useEffect(() => {
    if (tag === INSERT_TAG) {
      return;
    }

    const data = decoded(info, true);
    setState((s) => ({
      ...s,
      productsNm: data.productsNm,
      imageFile: {},
      description: data.description,
      isView: true,
      preview: data.image,
      headerTitle: "상품 수정",
      buttonNm: "수정",
      isInsert: false,
      id: data.id,
      isGroupBuy: data.isGroupBuy,
      isLeft: data.isLeft,
      isRight: data.isRight,
      leftButtonNm: data.leftButtonNm,
      rightButtonNm: data.rightButtonNm,
      leftLink: data.leftLink,
      rightLink: data.rightLink,
    }));
    return resetState;
  }, []);
  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <Header title={state.headerTitle} isShare={false} />
        <div className={styles.infoInputArea}>
          <div className={styles.publishArea}>
            <div className={styles.starIcon4} />
            <DoubleRadioButton
              leftNm={"공동 구매"}
              rightNm={"상품 추천"}
              leftClick={() => {
                productsOnOff(true);
              }}
              rightClick={() => {
                productsOnOff(false);
              }}
            />
          </div>
          <div className={styles.categoryName}>
            <div className={styles.starIcon} />
            <div className={styles.categoryInput}>
              <input
                className={styles.categoryInputInput}
                placeholder="상품명을 입력해주세요"
                defaultValue={state.productsNm}
                onKeyUp={(e) => {
                  changeProductName(e);
                }}
              />
            </div>
          </div>
          <div className={styles.mainImage}>
            <div className={styles.mainImageContainer}>
              <div className={styles.starIcon1} />
              <div
                className={styles.mainImageInputArea}
                onClick={triggerFileInput}
              >
                <div className={styles.inputPlaceholder}>
                  {!state.preview && (
                    <span className={styles.mainImageMessage}>
                      상품 이미지를 업로드해주세요
                    </span>
                  )}
                  {state.preview && (
                    <img
                      className={styles.preview}
                      src={state.preview}
                      alt="미리보기"
                    />
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className={styles.mainInput}
                  onChange={(e) => {
                    fileChange(e);
                  }}
                />
                <div className={styles.imageArea}>
                  {!state.preview && <div className={styles.uploadIcon} />}
                  {state.preview && (
                    <button className={styles.cancelBtn} onClick={removeImage}>
                      <span className={styles.cancel}>X</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.categoryDescription}>
            <div className={styles.descriptionInput}>
              <textarea
                className={styles.descriptionInput3}
                placeholder="상품에 대한 설명을 입력해주세요"
                defaultValue={state.description}
                rows={1}
                style={{ overflow: "hidden" }}
                onKeyUp={(e) => {
                  changeDescription(e);
                }}
              />
            </div>
          </div>
          <div className={styles.box4}>
            <div className={styles.section5}>
              <div className={styles.box5} onClick={leftClick}>
                <div className={styles.group8}>
                  {state.isLeft && (
                    <img src={checkedImg} className={styles.checkedbox} />
                  )}
                </div>
              </div>
              <input
                className={
                  !state.isLeft
                    ? `${styles.buttonNameInput} ${styles.checkBoxContainerDisabled}`
                    : styles.buttonNameInput
                }
                defaultValue={state.leftButtonNm}
                placeholder={"좌측 버튼명을 입력해주세요"}
                onChange={(e) => {
                  leftBtnChange(e);
                }}
                disabled={!state.isLeft}
              />
            </div>
            <div className={styles.section6}>
              <div className={styles.section7} />
              <input
                className={
                  !state.isLeft
                    ? `${styles.buttonNameInput} ${styles.checkBoxContainerDisabled}`
                    : styles.buttonNameInput
                }
                defaultValue={state.leftLink}
                placeholder={"링크를 입력해주세요"}
                onChange={(e) => {
                  leftLinkChange(e);
                }}
                disabled={!state.isLeft}
              />
            </div>
          </div>
          <div className={styles.buttonArea}>
            <div className={styles.buttonNameArea}>
              <div className={styles.checkboxArea} onClick={rightClick}>
                <div className={styles.group8}>
                  {state.isRight && (
                    <img src={checkedImg} className={styles.checkedbox} />
                  )}
                </div>
              </div>
              <input
                className={
                  !state.isRight
                    ? `${styles.buttonNameInput} ${styles.checkBoxContainerDisabled}`
                    : styles.buttonNameInput
                }
                defaultValue={state.rightButtonNm}
                placeholder={"우측 버튼명을 입력해주세요"}
                onChange={(e) => {
                  rightBtnChange(e);
                }}
                disabled={!state.isRight}
              />
            </div>
            <div className={styles.linkInputArea}>
              <div className={styles.emptyBox} />
              <input
                className={
                  !state.isRight
                    ? `${styles.buttonNameInput} ${styles.checkBoxContainerDisabled}`
                    : styles.buttonNameInput
                }
                defaultValue={state.rightLink}
                placeholder={"링크를 입력해주세요"}
                onChange={(e) => {
                  rightLinkChange(e);
                }}
                disabled={!state.isRight}
              />
            </div>
          </div>
          <div className={styles.publishArea}>
            <div className={styles.starIcon4} />
            <PostRadioButton
              postClick={() => {
                radioOnOff(true);
              }}
              notPostClick={() => {
                radioOnOff(false);
              }}
            />
          </div>
        </div>
        <div className={styles.registerButtonArea}>
          <button
            className={styles.registerButton}
            onClick={state.isInsert ? insert : update}
          >
            <span className={styles.register}>{state.buttonNm}</span>
          </button>
        </div>
        <Footer />
      </div>
    </div>
  );
}
